import { useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/store/auth'
import { Badge, Button, Card, Input } from '@diwata/ironvale'

type FamiliarTier = 'sovereign' | 'adept' | 'guild'

function tierVariant(tier: string): FamiliarTier {
  if (tier === 'sovereign' || tier === 'adept' || tier === 'guild') return tier
  return 'guild'
}

function statusVariant(status: string) {
  if (status === 'active') return 'success' as const
  if (status === 'suspended') return 'error' as const
  return 'neutral' as const
}

export function RealmDetail() {
  const { realmId } = useParams<{ realmId: string }>()
  const api = useAuth((s) => s.api)()
  const qc = useQueryClient()

  const { data: realm } = useQuery({ queryKey: ['realm', realmId], queryFn: () => api.getRealm(realmId!), enabled: !!realmId })
  const { data: familiars = [], isLoading } = useQuery({ queryKey: ['realm-familiars', realmId], queryFn: () => api.listRealmFamiliars(realmId!), enabled: !!realmId })

  const [creating, setCreating] = useState(false)
  const [name, setName] = useState('')
  const [role, setRole] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function create() {
    setSaving(true)
    setError('')
    try {
      // Realm-scoped creation is Guild-tier only; the server refuses anything
      // else, and refuses keepers below Elder in the Realm's Circle.
      await api.createRealmFamiliar(realmId!, name, role)
      setCreating(false)
      setName('')
      setRole('')
      qc.invalidateQueries({ queryKey: ['realm-familiars', realmId] })
    } catch (e) {
      setError(String(e))
    } finally { setSaving(false) }
  }

  return (
    <div>
      <div className="cp-page-header">
        <div>
          <div className="cp-page-title">{realm?.name ?? '…'}</div>
          {realm?.description && <div className="cp-page-subtitle">{realm.description}</div>}
        </div>
        {!creating && (
          <Button size="sm" onClick={() => setCreating(true)}>+ Guild familiar</Button>
        )}
      </div>

      {creating && (
        <Card style={{ marginBottom: 20 }}>
          <div className="cp-form">
            <div className="cp-field">
              <label className="cp-label">Name</label>
              <Input placeholder="Familiar name" value={name} onChange={(e) => setName(e.target.value)} autoFocus />
            </div>
            <div className="cp-field">
              <label className="cp-label">Role <span style={{ fontWeight: 400, color: 'var(--ae-color-text-muted)' }}>(optional)</span></label>
              <textarea className="cp-textarea" placeholder="This familiar's purpose…" value={role} onChange={(e) => setRole(e.target.value)} />
            </div>
            {error && <p className="cp-error">{error}</p>}
            <div style={{ display: 'flex', gap: 8 }}>
              <Button variant="ghost" size="sm" onClick={() => setCreating(false)}>Cancel</Button>
              <Button size="sm" onClick={create} disabled={saving || !name}>
                {saving ? 'Creating…' : 'Create'}
              </Button>
            </div>
          </div>
        </Card>
      )}

      {isLoading && <p className="cp-empty">Loading…</p>}
      {!isLoading && familiars.length === 0 && <p className="cp-empty">No familiars in this Realm yet.</p>}

      <div className="cp-card-grid">
        {familiars.map((f) => (
          <Link key={f.id} to={`/familiar/${f.id}/chat`} className="cp-familiar-card">
            <div className="cp-familiar-name">{f.name}</div>
            <div className="cp-familiar-meta">
              <Badge variant={tierVariant(f.tier)}>{f.tier}</Badge>
              <Badge variant={statusVariant(f.status)}>{f.status}</Badge>
            </div>
          </Link>
        ))}
      </div>
    </div>
  )
}
